<template>
    <div class="page-container">

        <div class="legal-page">
            <router-link to="/about" class="back-link">
                <ArrowLeft :size="20" />
                <span>Back to About</span>
            </router-link>

            <h1>Privacy Policy</h1>
            <p class="last-updated">Last updated: {{ currentDate }}</p>

            <div class="content">
                <section>
                    <h2>1. Introduction</h2>
                    <p>At Curie ("we," "us," or "our"), we are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our HSC Chemistry practice platform.</p>
                    <p>By using Curie, you consent to the data practices described in this policy. If you do not agree with the terms of this Privacy Policy, please do not access or use our service.</p>
                </section>

                <section>
                    <h2>2. Information We Collect</h2>

                    <h3>2.1 Information You Provide</h3>
                    <p>When you register for an account or use our service, we may collect:</p>
                    <ul>
                        <li>Account information: email address, password (encrypted), display name and school</li>
                        <li>Educational preferences: topic preferences through our question filters</li>
                        <li>Learning data: answers you submit, time spent on questions, hints accessed, and performance on practice questions</li>
                    </ul>

                    <h3>2.2 Information Collected Automatically</h3>
                    <p>When you access our service, we automatically collect:</p>
                    <ul>
                        <li>Usage data: questions attempted, topics accessed, features used, and learning patterns</li>
                        <li>Device information: browser type, operating system, device type, and screen resolution</li>
                        <li>Log data: IP address, access times, pages viewed, and referring URLs</li>
                        <li>Performance metrics: page load times and error logs for service improvement</li>
                    </ul>

                    <h3>2.3 Information from Third Parties</h3>
                    <p>We may receive information from third-party services:</p>
                    <ul>
                        <li>Payment processors (Stripe): transaction status and subscription information for purchases</li>
                    </ul>
                </section>

                <section>
                    <h2>3. How We Use Your Information</h2>
                    <p>We use the information we collect to:</p>
                    <ul>
                        <li>Provide and maintain our educational services</li>
                        <li>Grade your responses and provide personalised feedback</li>
                        <li>Track your learning progress and identify areas for improvement</li>
                        <li>Validate answers and deliver hints when requested</li>
                        <li>Analyse usage patterns to improve our question database and AI systems</li>
                        <li>Prevent abuse, fraud, and maintain platform security</li>
                        <li>Send account-related notifications and service updates</li>
                        <li>Comply with legal obligations</li>
                    </ul>
                </section>

                <section>
                    <h2>4. AI and Answer Processing</h2>
                    <p>Curie uses artificial intelligence to evaluate your answers and provide feedback. When processing your responses:</p>
                    <ul>
                        <li>Individual answer data is not linked to your identity when marked</li>
                        <li>Performance patterns may be used to enhance question difficulty calibration</li>
                    </ul>
                </section>

                <section>
                    <h2>5. Data Sharing and Disclosure</h2>

                    <h3>5.1 We Do Not Sell Your Data</h3>
                    <p>Your personal information is never sold to third parties. We do not use your data for advertising purposes.</p>

                    <h3>5.2 Service Providers</h3>
                    <p>We share information with trusted service providers who assist in operating our platform:</p>
                    <ul>
                        <li>Database and authentication services</li>
                        <li>Payment processing (Stripe)</li>
                        <li>Hosting and infrastructure providers</li>
                        <li>Analytics services</li>
                    </ul>
                    <p>These providers are contractually obligated to protect your information and use it only for the services they provide to us.</p>

                    <h3>5.3 Aggregated Data</h3>
                    <p>We may share anonymised, aggregated data for educational research purposes. This data cannot be used to identify individual users.</p>

                    <h3>5.4 Legal Requirements</h3>
                    <p>We may disclose your information if required by law or in response to valid legal requests from public authorities.</p>
                </section>

                <section>
                    <h2>6. Cookies and Tracking Technologies</h2>
                    <p>We use cookies and similar technologies to:</p>
                    <ul>
                        <li>Keep you signed in to your account</li>
                        <li>Remember your preferences and settings</li>
                        <li>Understand how you use our service</li>
                        <li>Improve platform performance</li>
                    </ul>
                    <p>You can control cookies through your browser settings, though some features may not function properly if cookies are disabled.</p>
                </section>

                <section>
                    <h2>7. Data Security</h2>
                    <p>We implement industry-standard security measures to protect your personal information, including:</p>
                    <ul>
                        <li>Encryption of passwords and sensitive data</li>
                        <li>Secure HTTPS connections</li>
                        <li>Regular security assessments</li>
                        <li>Access controls and authentication measures</li>
                    </ul>
                    <p>However, no method of transmission over the Internet is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.</p>
                </section>

                <section>
                    <h2>8. Data Retention</h2>
                    <p>We retain your information for as long as your account is active or as needed to provide our services. Specific retention periods:</p>
                    <ul>
                        <li>Account data: retained until you request deletion</li>
                        <li>Learning progress: retained while your account is active</li>
                        <li>Guest session data: deleted after 30 days</li>
                    </ul>
                    <p>After account deletion, we may retain anonymised data for analytics and service improvement purposes.</p>
                </section>

                <section>
                    <h2>9. Your Rights</h2>
                    <p>Under Australian privacy law, you have the right to:</p>
                    <ul>
                        <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                        <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                        <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                        <li><strong>Data Portability:</strong> Request your data in a portable format</li>
                        <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing where applicable</li>
                    </ul>
                    <p>To exercise these rights, please contact us at <a :href="'mailto:' + config.contact.email">{{ config.contact.email }}</a>.</p>
                </section>

                <section>
                    <h2>10. Children's Privacy</h2>
                    <p>Our service is intended for HSC students, who are typically 16-18 years old. Users under 13 years of age are not permitted to use Curie. Users between 13 and 18 should have parental or guardian consent before creating an account.</p>
                </section>

                <section>
                    <h2>11. Changes to This Policy</h2>
                    <p>We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the new policy on this page and updating the "Last updated" date. Your continued use of the service after changes constitutes acceptance of the updated policy.</p>
                </section>

                <section>
                    <h2>12. Australian Privacy Principles</h2>
                    <p>We comply with the Australian Privacy Principles (APPs) contained in the Privacy Act 1988 (Cth). If you have concerns about how we handle your personal information, you may contact the Office of the Australian Information Commissioner (OAIC) at <a href="https://www.oaic.gov.au" target="_blank" rel="noopener">www.oaic.gov.au</a>.</p>
                </section>

                <section>
                    <h2>13. Contact Us</h2>
                    <p>If you have any questions about this Privacy Policy or our data practices, please contact us at:</p>
                    <p><strong>Email:</strong> <a :href="'mailto:' + config.contact.email">{{ config.contact.email }}</a></p>
                    <p><strong>Location:</strong> {{ config.contact.location }}</p>
                </section>
            </div>
        </div>

        <!-- Footer Section -->
        <FooterSection />
    </div>
</template>

<script>
import FooterSection from '@/components/home/FooterSection.vue';
import { ArrowLeft } from 'lucide-vue-next';
import { SITE_CONFIG } from '@/config/site';

export default {
    name: 'PrivacyView',
    components: {
        FooterSection,
        ArrowLeft
    },
    data() {
        return {
            config: SITE_CONFIG
        };
    },
    computed: {
        currentDate() {
            return new Date().toLocaleDateString('en-AU', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        }
    }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap');

* {
    font-family: "Space Grotesk", sans-serif;
    font-weight: 400;
    letter-spacing: 1px;
    box-sizing: border-box;
}

.page-container {
    position: relative;
    overflow-x: hidden;
    min-height: 100vh;
    background: var(--bg-primary);
}

.legal-page {
    position: relative;
    z-index: 2;
    max-width: 800px;
    margin: 0 auto;
    padding: 80px 20px;
}

.back-link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: var(--accent);
    text-decoration: none !important;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 40px;
    transition: transform 0.2s ease;
}

.back-link:hover {
    transform: translateX(-4px);
}

h1 {
    font-size: 48px;
    color: var(--text-primary);
    margin-bottom: 12px;
}

.last-updated {
    color: var(--text-secondary);
    font-size: 14px;
    margin-bottom: 50px;
}

.content {
    color: var(--text-secondary);
    line-height: 1.8;
}

section {
    margin-bottom: 40px;
}

h2 {
    font-size: 22px;
    color: var(--text-primary);
    margin-bottom: 16px;
    font-weight: 600;
}

h3 {
    font-size: 18px;
    color: var(--text-primary);
    margin-top: 24px;
    margin-bottom: 12px;
    font-weight: 500;
}

p {
    font-size: 16px;
    margin-bottom: 16px;
}

ul {
    margin-left: 24px;
    margin-bottom: 16px;
}

li {
    margin-bottom: 8px;
    font-size: 16px;
}

a {
    color: var(--accent);
    text-decoration: none;
}

a:hover {
    text-decoration: underline;
}

strong {
    color: var(--text-primary);
    font-weight: 600;
}

/* Scrollbar */
::-webkit-scrollbar {
    width: 12px;
}

::-webkit-scrollbar-track {
    background: var(--bg-primary);
}

::-webkit-scrollbar-thumb {
    background: var(--accent);
    border-radius: 6px;
}

@media (max-width: 768px) {
    .legal-page {
        padding: 40px 20px;
    }

    h1 {
        font-size: 36px;
    }

    h2 {
        font-size: 20px;
    }

    h3 {
        font-size: 17px;
    }
}
</style>
